import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { queryClient } from "@/libs/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { useCreateNavigationItem } from "../api/create-navigation-item";
import { useUpdateNavigationItem } from "../api/update-navigation-item";
import { NavigationItemFormProps } from "../types/navigation-item-form";
import {
  NoDataNavigationItemFormData,
  noDataNavigationItemSchema,
} from "../types/no-data-navigation-item-schema";

import { DeleteNavigationItemDialog } from "./delete-navigation-item-dialog";
import { IconSelector } from "./icon-selector";

function NoDataNavigationItemForm(props: NavigationItemFormProps) {
  const { t } = useTranslation();
  const { initialData, menuId, menuItemType, onClose } = props;
  const { congress } = useCongress();
  const form = useForm<NoDataNavigationItemFormData>({
    resolver: zodResolver(noDataNavigationItemSchema),
    defaultValues: {
      title: initialData ? initialData.title : "",
      icon: initialData?.icon ? initialData.icon : undefined,
      visibility: initialData?.visibility ?? "",
    },
  });

  const { mutateAsync: updateNavigationItem } = useUpdateNavigationItem();
  const { mutateAsync: createNavitationItem } = useCreateNavigationItem();

  useEffect(() => {
    form.reset();
  }, [menuItemType, form]);

  //TODO: handler server error
  const onSubmit = async (values: NoDataNavigationItemFormData) => {
    if (initialData) {
      await updateNavigationItem({
        requestBody: values,
        menuItemId: initialData.id,
      });
    } else {
      await createNavitationItem({
        menuId,
        requestBody: { ...values, type: menuItemType, data: null },
      });
    }

    /**
     * TODO: refetch properly. in *-navigatin-item-form.tsx call with congressId
     */
    await queryClient.refetchQueries({
      queryKey: ["navigation", congress.id],
    });
    onClose();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("navItem.title")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("navItem.title")}
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="icon"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="block">{t("navItem.icon")}</FormLabel>
                <FormControl>
                  <IconSelector
                    onSelect={(value) =>
                      form.setValue("icon", value, { shouldValidate: true })
                    }
                    value={field.value}
                    disabled={form.formState.isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="visibility"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("navItem.visibility")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("navItem.visibilityPlaceholder")}
                    className="h-8 px-2 py-1"
                    disabled={form.formState.isSubmitting}
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex">
            <Button
              size="sm"
              disabled={!form.formState.isValid || form.formState.isSubmitting}
            >
              {t("navItem.save")}
            </Button>
            {!initialData && (
              <Button
                size="sm"
                variant="link"
                type="button"
                className="ml-auto"
                onClick={onClose}
              >
                {t("navItem.cancel")}
              </Button>
            )}
            {initialData && (
              //display delete button only existing menu items
              <DeleteNavigationItemDialog menuItemId={initialData.id} />
            )}
          </div>
        </div>
      </form>
    </Form>
  );
}

export { NoDataNavigationItemForm };
